import { Banner, Button, Tag } from '@dev-spendesk/grapes';
import { differenceInMinutes } from 'date-fns';
import { useEffect, useState } from 'react';

import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useFetchKycRemediation } from 'modules/invoices/hooks/useFetchKycRemediation';
import { isEeaMember } from 'src/core/config/country';
import FEATURES from 'src/core/constants/features';
import { LocalStorageKey } from 'src/core/constants/storage';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

const trackNotInterested = () => {
  track(AnalyticEventName.SETTINGS_PAYMENT_METHODS_IWT_AWARENESS_SKIPPED, {
    source: 'Banner',
  });
};

const trackAcknowledged = () => {
  track(AnalyticEventName.SETTINGS_PAYMENT_METHODS_IWT_AWARENESS_ACKNOWLEDGED, {
    source: 'Banner',
  });
};

export const InternationalWireTransferFeatureAdoptionBanner = () => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const isEEAOrEUROCurrency =
    company.currency === 'EUR' || isEeaMember(company.country);
  const isEeaInternationalTransfersEnabled = useFeature(
    FEATURES.TMP_EEA_INTERNATIONAL_WIRE_TRANSFER,
  );
  const hasBeenClosed = localStorage.getItem(
    LocalStorageKey.IwtAdoptionBannerClosed,
  );
  const adoptionModalClosedDate = localStorage.getItem(
    LocalStorageKey.IwtAdoptionModalClosedDate,
  );
  const wasAdoptionModalClosedLongAgo = adoptionModalClosedDate
    ? differenceInMinutes(new Date(), new Date(adoptionModalClosedDate)) > 10
    : false;
  const fetchKycRemediationResult = useFetchKycRemediation({
    isEnabled:
      isEeaInternationalTransfersEnabled &&
      isEEAOrEUROCurrency &&
      !hasBeenClosed &&
      wasAdoptionModalClosedLongAgo,
  });

  useEffect(() => {
    if (
      fetchKycRemediationResult.status === 'success' &&
      fetchKycRemediationResult.data.status === 'approved'
    ) {
      setIsOpen(true);
    }
  }, [fetchKycRemediationResult]);

  const closeBanner = () => {
    setIsOpen(false);
    localStorage.setItem(LocalStorageKey.IwtAdoptionBannerClosed, 'true');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Banner
      variant="brand"
      title={
        <div className="flex items-center gap-8">
          <h3 className="title-m">
            {t('international-wiretransfer.adoptionBanner.title')}
          </h3>
          <Tag variant="ocean">{t('misc.new')}</Tag>
        </div>
      }
      actions={
        <>
          <Button
            className="pl-0"
            variant="tertiaryNeutral"
            text={t(
              'international-wiretransfer.adoptionModal.eea.notInterested',
            )}
            onClick={() => {
              trackNotInterested();
              closeBanner();
            }}
          />
          <Button
            variant="secondaryNeutral"
            text={t('misc.gotIt')}
            onClick={() => {
              trackAcknowledged();
              closeBanner();
            }}
          />
        </>
      }
    >
      {t('international-wiretransfer.adoptionBanner.description')}
    </Banner>
  );
};
